
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlinePlay,
  HiOutlineVideoCamera,
  HiOutlineArrowUpRight,
  HiOutlineFilm,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   IMAGE SOURCES

   Support the current WebP filenames
   and older PNG/JPG database records.
============================================ */

function getVideoImageSources(image) {
  if (!image || typeof image !== "string") {
    return [];
  }

  const original = image.trim().replace(/\\/g, "/");

  if (!original) return [];

  if (/^(https?:\/\/|data:image\/|blob:)/i.test(original)) {
    return [original];
  }

  const path = original.replace(/^\/+/, "");

  const webpPath = path.replace(
    /\.(png|jpe?g)$/i,
    ".webp"
  );

  const paths = [...new Set([webpPath, path])];

  const base = String(media || "").replace(/\/+$/, "");

  return [
    ...new Set(
      paths.flatMap((filePath) => {
        const filename = filePath.split("/").pop();

        return [
          base
            ? `${base}/${filePath}`
            : `/${filePath}`,

          `/images/${filename}`,
          `/${filePath}`,
        ];
      })
    ),
  ];
}

/* ============================================
   DESCRIPTION HELPER
============================================ */

function getPlainText(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   VIDEO CATEGORY IMAGE
============================================ */

function VideoCategoryThumbnail({ image }) {
  const sources = useMemo(
    () => getVideoImageSources(image),
    [image]
  );

  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [image]);

  const currentSource = sources[sourceIndex];

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#102739]">

      {currentSource ? (
        <img
          src={currentSource}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => {
            setSourceIndex((previous) => previous + 1);
          }}
          className="
            h-full w-full
            object-cover object-center

            transition-transform
            duration-700 ease-out

            group-hover:scale-[1.06]
          "
        />
      ) : (
        /* Elegant fallback */
        <div
          className="
            absolute inset-0
            flex items-center justify-center

            bg-gradient-to-br
            from-[#294961]
            via-[#18354A]
            to-[#0B1726]
          "
        >
          <div
            aria-hidden="true"
            className="
              absolute -end-20 -top-20
              h-64 w-64
              rounded-full
              border border-white/10
            "
          />

          <div
            aria-hidden="true"
            className="
              absolute -start-24 -bottom-24
              h-64 w-64
              rounded-full
              border border-white/10
            "
          />

          <HiOutlineFilm
            size={66}
            className="text-[#D7B77F]/50"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Image tint */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0

          bg-gradient-to-t
          from-[#061522]/65
          via-[#061522]/10
          to-[#061522]/20
        "
      />
    </div>
  );
}

/* ============================================
   VIDEO CATEGORY CARD
============================================ */

function VideoCategoryCard({
  category,
  index = 0,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? category?.titleEn || category?.titleAr
    : category?.titleAr || category?.titleEn;

  const rawDescription = isEnglish
    ? category?.textEn || category?.textAr
    : category?.textAr || category?.textEn;

  const description = getPlainText(rawDescription);

  const content = {
    eyebrow: isEnglish
      ? "VIDEO COLLECTION"
      : "مجموعة فيديوهات",

    explore: isEnglish
      ? "Explore Videos"
      : "استعرض الفيديوهات",

    fallbackDescription: isEnglish
      ? "Explore educational legal videos covering the topics in this category."
      : "مجموعة من الفيديوهات القانونية التي تتناول موضوعات هذا التصنيف.",
  };

  const number = String(index + 1).padStart(2, "0");

  return (
    <Link
      to="/VideosPage"
      state={{ category }}
      onClick={() => {
        window.scrollTo(0, 0);
      }}
      aria-label={`${content.explore}: ${title}`}
      className="
        group flex h-full
        min-w-0 flex-col
        overflow-hidden

        rounded-[22px]

        border border-[#D9E4EC]
        bg-white

        shadow-[0_10px_35px_rgba(15,39,55,0.05)]

        transition-all duration-300

        hover:-translate-y-1.5
        hover:border-[#C19B60]/45
        hover:shadow-[0_20px_50px_rgba(15,39,55,0.12)]

        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-[#C19B60]

        dark:border-white/10
        dark:bg-[#1B3549]
        dark:shadow-none

        dark:hover:border-[#D7B77F]/35
      "
    >
      {/* ======================================
          VIDEO THUMBNAIL
      ====================================== */}

      <div
        className="
          relative aspect-video
          w-full shrink-0
          overflow-hidden
          bg-[#102739]
        "
      >
        {/* Image */}
        <VideoCategoryThumbnail image={category?.Image} />

        {/* Category number */}
        <div
          className="
            absolute end-4 top-4

            flex h-9 min-w-9
            items-center justify-center

            rounded-lg
            border border-white/20
            bg-[#102031]/65
            px-3

            font-cairo text-xs
            font-semibold tabular-nums
            text-white
            backdrop-blur-lg
          "
        >
          {number}
        </div>

        {/* Play emblem */}
        <div
          aria-hidden="true"
          className="
            absolute left-1/2 top-1/2
            flex h-[66px] w-[66px]
            -translate-x-1/2
            -translate-y-1/2
            items-center justify-center

            rounded-full

            border border-white/40
            bg-white/15

            text-white
            backdrop-blur-lg

            shadow-[0_10px_30px_rgba(0,0,0,0.18)]

            transition-all duration-300

            group-hover:scale-110
            group-hover:border-white/60
            group-hover:bg-white/25
          "
        >
          <HiOutlinePlay
            size={29}
            className="ms-0.5"
          />
        </div>

        {/* Bottom label */}
        <div
          className="
            absolute bottom-4 start-5
            flex items-center gap-2

            rounded-lg
            border border-white/15
            bg-[#102031]/50

            px-3 py-1.5
            backdrop-blur-lg
          "
        >
          <HiOutlineVideoCamera
            size={16}
            className="text-[#D7B77F]"
            aria-hidden="true"
          />

          <span className="font-cairo text-xs font-semibold text-white">
            {content.eyebrow}
          </span>
        </div>
      </div>

      {/* ======================================
          CARD INFORMATION
      ====================================== */}

      <div
        className="
          flex flex-1 flex-col
          px-6 pb-6 pt-7
          sm:px-7 sm:pb-7
        "
      >
        {/* Top label */}
        <div className="mb-4 flex items-center gap-3">
          <span
            aria-hidden="true"
            className="
              h-px w-8
              bg-[#C19B60]
              dark:bg-[#D7B77F]
            "
          />

          <span
            className="
              font-cairo text-xs
              font-semibold

              text-[#A47D41]
              dark:text-[#D7B77F]
            "
          >
            {content.eyebrow}
          </span>
        </div>

        {/* Title */}
        <h3
          className="
            font-cairo
            text-xl font-bold
            leading-[1.8]

            line-clamp-2

            text-[#19344D]
            dark:text-[#F5F8FB]

            transition-colors duration-300

            group-hover:text-[#A47D41]
            dark:group-hover:text-[#E5C995]
          "
        >
          {title}
        </h3>

        {/* Description */}
        <p
          className="
            mt-4 line-clamp-3
            min-h-[70px]

            font-cairo text-sm
            leading-7

            text-[#62778A]
            dark:text-[#B5C8D6]
          "
        >
          {description || content.fallbackDescription}
        </p>

        {/* ======================================
            CARD FOOTER
        ====================================== */}

        <div className="mt-auto pt-7">

          <div className="mb-5 h-px bg-[#DFE7ED] dark:bg-white/10" />

          <div
            className="
              flex items-center
              justify-between gap-4
            "
          >
            {/* Action title */}
            <span
              className="
                font-cairo text-sm
                font-semibold

                text-[#19344D]
                dark:text-white
              "
            >
              {content.explore}
            </span>

            {/* Action arrow */}
            <span
              className="
                flex h-10 w-10
                shrink-0 items-center
                justify-center

                rounded-xl
                border border-[#D9E4EC]
                bg-[#F0F5F8]

                text-[#19344D]

                transition-all duration-300

                group-hover:border-[#19344D]
                group-hover:bg-[#19344D]
                group-hover:text-white

                dark:border-white/10
                dark:bg-white/[0.07]
                dark:text-white
                dark:group-hover:border-white/30
                dark:group-hover:bg-white/15
              "
            >
              <HiOutlineArrowUpRight
                size={19}
                aria-hidden="true"
                className="directional-arrow"
              />
            </span>
          </div>

        </div>
      </div>
    </Link>
  );
}

export default VideoCategoryCard;
